/**
 * Client-side calls to the email/password auth endpoints. The OAuth
 * (Google / Apple) flows are top-level navigations to the backend, so
 * they don't need an axios client — see `oauthHref` below.
 */
import { api } from './api';
import type { CurrentUser } from './auth';

export interface RegisterArgs {
  email: string;
  password: string;
  displayName: string;
  locale?: string;
}

export interface RegisterResponse {
  user: CurrentUser & { emailVerified: boolean };
  requireVerification: boolean;
}

export async function register(args: RegisterArgs): Promise<RegisterResponse> {
  const r = await api.post<RegisterResponse>('/api/auth/register', args);
  return r.data;
}

export interface LoginArgs {
  email: string;
  password: string;
}

export interface LoginResponse {
  /** Present on a completed (no-MFA) login. */
  user?: CurrentUser & { emailVerified: boolean };
  /** Present when the account has TOTP enabled — exchange via verifyMfaLogin. */
  mfaRequired?: boolean;
  mfaToken?: string;
}

export async function login(args: LoginArgs): Promise<LoginResponse> {
  const r = await api.post<LoginResponse>('/api/auth/login', args);
  return r.data;
}

// ── MFA / TOTP ──

export async function verifyMfaLogin(mfaToken: string, code: string): Promise<{ user: CurrentUser & { emailVerified: boolean } }> {
  const r = await api.post<{ user: CurrentUser & { emailVerified: boolean } }>('/api/auth/mfa/verify-login', { mfaToken, code });
  return r.data;
}

export async function mfaStatus(): Promise<{ enabled: boolean }> {
  const r = await api.get<{ enabled: boolean }>('/api/auth/mfa/status');
  return r.data;
}

export async function mfaEnroll(): Promise<{ otpauthUrl: string; secretBase32: string }> {
  const r = await api.post<{ otpauthUrl: string; secretBase32: string }>('/api/auth/mfa/enroll');
  return r.data;
}

export async function mfaActivate(code: string): Promise<{ recoveryCodes: string[] }> {
  const r = await api.post<{ recoveryCodes: string[] }>('/api/auth/mfa/activate', { code });
  return r.data;
}

export async function mfaDisable(code: string): Promise<void> {
  await api.post('/api/auth/mfa/disable', { code });
}

export async function forgotPassword(email: string): Promise<void> {
  await api.post('/api/auth/forgot-password', { email });
}

export async function resetPassword(token: string, password: string): Promise<void> {
  await api.post('/api/auth/reset-password', { token, password });
}

export async function verifyEmail(token: string): Promise<void> {
  await api.post('/api/auth/verify-email', { token });
}

export async function resendVerification(): Promise<void> {
  await api.post('/api/auth/resend-verification');
}

export async function updateProfile(input: { displayName?: string; avatarUrl?: string }): Promise<CurrentUser> {
  const r = await api.patch<CurrentUser>('/api/auth/me', input);
  return r.data;
}

export async function uploadAvatar(file: Blob): Promise<CurrentUser> {
  const fd = new FormData();
  fd.append('file', file, 'avatar.png');
  const r = await api.post<CurrentUser>('/api/auth/me/avatar', fd);
  return r.data;
}

/**
 * Returns the absolute URL that initiates a provider OAuth flow.
 * In dev that's `http://localhost:3402/auth/google`; in production
 * that's `https://keenez.com/auth/google` — Vite swaps
 * VITE_API_URL at build time. OAuth start routes are excluded from
 * the global `/api` prefix on the backend.
 */
export function oauthHref(provider: 'google' | 'apple'): string {
  const base = (import.meta.env.VITE_API_URL as string | undefined) || '';
  return `${base}/auth/${provider}`;
}

/** Translate axios errors into a user-readable message. */
export function readApiError(err: unknown, fallback: string): string {
  const r = err as { response?: { data?: { message?: string | string[] } } };
  const msg = r?.response?.data?.message;
  if (Array.isArray(msg)) return msg[0] || fallback;
  if (typeof msg === 'string') return msg;
  return fallback;
}
